<template>
   <div class="container py-5 h-100">
    <div class="row d-flex justify-content-center align-items-center h-100">
      <div class="col-12 col-md-8 col-lg-6 col-xl-5">
        <div class="card text-black" style="border-radius: 1rem;">
          <div class="card-body p-5 text-center">
            <div class="card-body text-center">
              <div>
                <img :src="require('@/assets/img/product/core.png')" class="img-fluid" />
              </div>
              <div>
                <div class="spinner-border text-info" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="text-muted">verifying access..</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import jwt_decode from 'jwt-decode'

  export default {
    name: 'Validation',

    data() {
      return {
        hasTraining: false,
        hasPhishing: false,
        hasPentest: false,
        isVertexAnalyst: null,
        organisations: [],
        isSuperAdmin: false,
        firstLogin: false,
      }
    },

    async created() {
      await this.validate()
    },

    methods: {
      async validate() {
        try {
          // Exit if no token is found
          const access_token = this.$route.query.access_token;
          const targetOrgId = this.$route.query.org_selected || this.$route.query.org_id;

          if (!access_token) {
            if (this.$store.state.auth.authenticated && this.$store.state.auth.accessToken) {
              this.redirectToAppropriatePage();
              return;
            }
            this.$store.dispatch('auth/logout', { logoutAuth: false });
            return;
          }

          // Decode the token
          let decoded = null;
          try {
            decoded = jwt_decode(access_token);
          } catch (error) {
            console.error("Invalid token:", error);
            this.$store.dispatch('auth/logout', { logoutAuth: false });
            return;
          }

          // Check if token is expired
          let isExpired = false;
          if (decoded) {
            if (decoded.exp && (decoded.exp * 1000 <= Date.now())) {
              isExpired = true;
            } else if (decoded.expires_at) {
              const expDate = new Date(decoded.expires_at);
              if (!isNaN(expDate.getTime()) && expDate.getTime() <= Date.now()) {
                isExpired = true;
              }
            }
          }

          if (isExpired) {
            this.$store.commit('auth/setTokenExpires');
            this.$store.dispatch('auth/logout', { logoutAuth: true });
            return;
          }

          this.$store.commit("auth/setAccessToken", access_token);
          this.$store.commit("auth/setJwtPayload", decoded);

          // Set product access after jwt decode
          this.setProductAccess();

          // Check roles and set login loop
          this.setLoginLoop();

          // Check if user has required roles
          if (!this.hasValidRoles(decoded)) {
            this.$toast.error("User has no valid role. Please contact administrator.");
            this.$store.dispatch('auth/logout', { logoutAuth: false });
            return;
          }

          // Set superadmin flag
          this.isSuperAdmin = this.$store.state.auth.isSuperAdmin;

          // Set isVertexAnalyst flag
          this.isVertexAnalyst = this.$store.state.auth.accessType === "vertex_analyst";

          // Fetch organizations in parallel only when needed
          if (this.isSuperAdmin || this.isVertexAnalyst) {
            await this.fetchOrganisations();
            await this.handleSuperAdminAndAnalyst(decoded);
          } else {
            await this.handleNonAdmin(decoded, targetOrgId);
          }

          this.$store.commit("auth/authenticated", true);

          if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }

          // Fetch active products in the background so image payloads do not stall validation redirect
          const orgID = this.$helper.setOrganisationID();
          if (orgID) {
            this.getProducts(orgID).then(products => {
              if (products) {
                this.$store.commit("auth/setOrgProducts", products);
              }
            }).catch(error => {
              console.error("Background getProducts error:", error);
            });
          }

          // Redirect based on recent URL or roles
          this.redirectToAppropriatePage();
        } catch (error) {
          console.error("Validation Error:", error);
          this.$toast.error("An error occurred during authentication validation.");
          this.$store.dispatch('auth/logout', { logoutAuth: false });
        }
      },

      // Check if user has valid roles
      hasValidRoles(decoded) {
        return Boolean(
          this.$store.state.auth.isSuperAdmin ||
          (decoded && Array.isArray(decoded.organisation_role) && decoded.organisation_role.length > 0)
        );
      },

      // Set login loop based on query
      setLoginLoop() {
        const loginLoop = this.$route.query.login_loop || 0;
        this.$store.commit("auth/setLoginLoop", loginLoop);
      },

      // Fetch organizations if not already available
      async fetchOrganisations() {
        if (!this.organisations.length) {
          this.organisations = await this.getOrganisations();
        }
      },

      // Handle logic for super admin and vertex analyst
      async handleSuperAdminAndAnalyst(decoded) {
        const orgIdFromQuery = this.$route.query.org_id;
        let organisationToSet;

        if (orgIdFromQuery) {
          organisationToSet = this.organisations.find(org => org.id == orgIdFromQuery);
        }

        if (this.isVertexAnalyst) {
          const orgRole = decoded && Array.isArray(decoded.organisation_role) ? decoded.organisation_role[0] : null;
          const organisation = orgRole ? this.organisations.find(org => org.id == orgRole.organisation_id) : null;
          if (orgRole) {
            const analystOrg = {
              name: organisation?.name || orgRole.organisation || 'Organisation',
              id: organisation?.id || orgRole.organisation_id
            };
            localStorage.setItem('userOrganisation', JSON.stringify(analystOrg));
          }
          if (organisation) {
            organisation.products = [...(organisation.products || []), orgRole.products];
            if (!organisationToSet) {
              this.$store.commit("user/setOrganisation", organisation);
            }
          }
          if (organisationToSet) {
            if (orgRole && orgRole.products) {
              organisationToSet.products = [...(organisationToSet.products || []), orgRole.products];
            }
            this.$store.commit("user/setOrganisation", organisationToSet);
          }
        } else if (organisationToSet) {
          this.$store.commit("user/setOrganisation", organisationToSet);
        } else { // Super Admin fallback
          // If no org_id is provided, default to the first organisation.
          if (this.organisations && this.organisations.length > 0) {
            this.$store.commit("user/setOrganisation", this.organisations[0]);
          }
        }

        this.$store.commit("user/setOrganisations", this.organisations);
      },

      // Handle logic for non-admin roles
      async handleNonAdmin(decoded, targetOrgId) {
        if (!decoded || !Array.isArray(decoded.organisation_role) || decoded.organisation_role.length === 0) {
          throw new Error("No organization role found for non-admin user.");
        }

        const coreProducts = [
          'automated_test_phishing_campaigns',
          'cyber_security_online_awareness_training',
          'email_compromise_check',
          'policy_management',
        ];

        let orgRole = null;
        if (targetOrgId) {
          orgRole = decoded.organisation_role.find(
            (o) => String(o.organisation_id || o.id) === String(targetOrgId)
          );
        }

        if (!orgRole) {
          orgRole = decoded.organisation_role.find(
            (o) => Array.isArray(o.products) && o.products.some((p) => coreProducts.includes(p.product))
          );
        }

        if (!orgRole) {
          orgRole = decoded.organisation_role[0];
        }

        if (!orgRole) {
          throw new Error("No organization role found for non-admin user.");
        }

        const resolvedOrgId = orgRole.organisation_id || orgRole.id;
        const normalizedOrgRole = {
          ...orgRole,
          id: resolvedOrgId,
          organisation_id: resolvedOrgId,
        };

        if (normalizedOrgRole.organisation_logo) {
          delete normalizedOrgRole.organisation_logo;
        }

        // Commit immediately before fetching organization details
        this.$store.commit("user/setOrganisation", normalizedOrgRole);

        const organisation = await this.getOrganisation(resolvedOrgId);

        this.$store.commit("user/setBambooState", Boolean(organisation.bamboo_hr_api_key));

        this.$store.commit("user/setOrganisation", {
          ...normalizedOrgRole,
          ...organisation,
          id: organisation?.id || resolvedOrgId,
          organisation_id: organisation?.organisation_id || organisation?.id || resolvedOrgId,
        });

        if (organisation.org_status === "Active" || organisation.org_status === "active") {
          if (this.hasTraining) {
            await this.getUserVideos(decoded.user_id, resolvedOrgId);
            if (this.firstLogin) {
              await this.$axios.post(`/trainings/users/${decoded.user_id}/organisations/${resolvedOrgId}/assign-video`);
            }
          }
        }
      },

      // Navigate post-login and trap initial back-button bounce to external auth login
      async navigatePostLogin(targetPath) {
        try {
          await this.$router.replace(targetPath);
          if (typeof window !== 'undefined' && window.history && window.history.pushState) {
            window.history.pushState({ postLogin: true }, document.title, window.location.href);
          }
        } catch (e) {
          console.error('Post-login navigation error:', e);
        }
      },

      // Redirect based on role or recent URL
      redirectToAppropriatePage() {
        const orgProducts = this.$store.state.user.organisation?.products || [];
        const isAdmin = this.isSuperAdmin || this.isCoreAdmin(orgProducts, 'admin') || this.isVertexAnalyst;
        const recentUrl = localStorage.getItem('currentUrl');

        if (recentUrl && recentUrl !== 'null' && recentUrl !== 'undefined' && !recentUrl.startsWith('/validation')) {
          // Remove consumed currentUrl so subsequent logins don't get stuck on stale redirects
          localStorage.removeItem('currentUrl');

          // Handle the case when a recent URL exists
          if (recentUrl.includes('/dashboard')) {
            // If the recent URL is '/dashboard', check if the user is not an admin and not a manager
            if (!isAdmin && !this.$store.state.auth.isManager) {
              // Redirect to the "else" path if user is not an admin or a manager
              this.navigatePostLogin('/training/assigned-training');
              return;
            } else {
              // Otherwise, allow access to the dashboard (admin or manager)
              this.navigatePostLogin(recentUrl);
              return;
            }
          } else {
            // If the recent URL is not '/dashboard', just redirect to it as usual (outer source link)
            this.navigatePostLogin(recentUrl);
            return;
          }
        }

        // Clean up any invalid or stale URL
        localStorage.removeItem('currentUrl');

        // Handle navigation for super admin
        if (this.isSuperAdmin) {
          this.navigatePostLogin('/dashboard');
          return;
        }

        // Handle navigation for admin
        if (isAdmin) {
          if (!this.hasTraining && this.hasPhishing) {
            this.navigatePostLogin('/phishing/campaign-settings');
          } else {
            this.navigatePostLogin('/dashboard');
          }
          return;
        }

        // Handle navigation for manager or general user
        if (this.$store.state.auth.isManager && this.$store.state.auth.isGeneralUser) {
          this.navigatePostLogin('/dashboard');
          return;
        }

        // Default redirection for all other cases
        this.navigatePostLogin('/training/assigned-training');
      },


      // Get organisations from API
      async getOrganisations() {
        try {
          const response = await this.$axios.get(`/supadmin/organisations`);
          return response.data.success ? response.data.organisations : [];
        } catch (error) {
          this.$toast.error('Failed to load organisations.');
          return [];
        }
      },

      // Get organisation from API
      async getOrganisation(id) {
        try {
          const response = await this.$axios.get(`/organisations/${id}/validation`);
          return response.data.success ? response.data.organisation : {};
        } catch (error) {
          this.$toast.error('Failed to load organisation.');
          return {};
        }
      },

      // Get user videos from API
      async getUserVideos(userID, orgID) {
        try {
          const response = await this.$axios.get(`/trainings/users/${userID}/organisations/${orgID}/videos`);
          this.firstLogin = response.data.training_videos.length === 0;
        } catch (error) {
          this.$toast.error('Failed to load training videos.');
        }
      },

      async getProducts(orgID) {
        if (!orgID) return null;
        try {
          const response = await this.$axios.get(`/organisations/${orgID}/products`);
          if (response.data.success) {
            return response.data.products; // Correct return value
          } else {
            this.$toast.error(response.data.message);
            return null; // Ensure a return value in case of failure
          }
        } catch (error) {
          this.$toast.error("Failed to fetch products");
          console.error(error);
          return null;
        }
      },

      // Core admin check
      isCoreAdmin(products, role){
        if (!Array.isArray(products)) return false;
        var isExist = false;
        products.forEach(product => {
          if (product && (product.product == "email_compromise_check" || product.product == "cyber_security_online_awareness_training" || product.product == "automated_test_phishing_campaigns")) {
            if (product.roles && product.roles.includes(role)) isExist = true;
          }
        });

        return isExist;
      },

      filterOrganisations(organisations){
        if (!this.$route.query.org_selected){
          this.$store.commit("user/setOrganisation", organisations[0]);
          return
        }

        var orgIndex = -1
        for (let index = 0; index < organisations.length; index++) {
          if (organisations[index].id == this.$route.query.org_selected){
            orgIndex = index
            break;
          }
        }

        if (orgIndex != -1) {
          //delete organisation_logo
          delete organisations[orgIndex].organisation_logo

          this.$store.commit("user/setOrganisation", organisations[orgIndex]);
        } else {
          //delete organisation_logo
          delete organisations[0].organisation_logo

          this.$store.commit("user/setOrganisation", organisations[0]);
        }
      },

      setProductAccess(){
        this.hasPentest = this.$store.state.auth.hasPentest;
        this.hasTraining = this.$store.state.auth.hasTraining;
        this.hasPhishing = this.$store.state.auth.hasPhishing;
      }
    },

    // watch: {
    //   '$route': {
    //     immediate: true,
    //     handler() {
    //       this.validate();
    //     }
    //   }
    // },
  }
</script>
